import { AbstractShape } from "../abstract/Shape";

export class Camera {
    offsetX: number;
    offsetY: number;
    scale: number;
    isPanning: boolean;
    lastMouseX: number;
    lastMouseY: number;

    constructor() {
        this.offsetX = 0;
        this.offsetY = 0;
        this.scale = 1;
        this.isPanning = false;
        this.lastMouseX = 0;
        this.lastMouseY = 0;
    }

    /**
     * Adjusts a screen coordinate to logical canvas coordinates.
     */
    toCanvasCoordinates(x: number, y: number): { x: number; y: number } {
        return {
            x: (x - this.offsetX) / this.scale,
            y: (y - this.offsetY) / this.scale,
        };
    }

    cameraPanning(e: MouseEvent, drawCallback: () => void) {
        const deltaX = e.offsetX - this.lastMouseX;
        const deltaY = e.offsetY - this.lastMouseY;

        this.offsetX += deltaX;
        this.offsetY += deltaY;

        this.lastMouseX = e.offsetX;
        this.lastMouseY = e.offsetY;

        drawCallback();
    }

    /**
     * Binds event listeners for panning and zooming.
     */
    bindEvents(canvas: HTMLCanvasElement, drawCallback: () => void, signal?: AbortSignal) {
        canvas.addEventListener('wheel', (e) => {
            e.preventDefault();
        
            const zoomIntensity = 0.1; // 10% zoom per wheel tick
            const scaleFactor = Math.exp(e.deltaY * -zoomIntensity * 0.01); // smooth zooming
        
            const oldScale = this.scale;
            this.scale = Math.max(0.001, Math.min(50, this.scale * scaleFactor));
        
            // Get mouse position relative to canvas
            const mouseX = e.offsetX;
            const mouseY = e.offsetY;
        
            // Calculate the logical mouse position before zoom
            const logicalMouseX = (mouseX - this.offsetX) / oldScale;
            const logicalMouseY = (mouseY - this.offsetY) / oldScale;
        
            // Adjust camera offset to keep mouse position fixed
            this.offsetX = mouseX - logicalMouseX * this.scale;
            this.offsetY = mouseY - logicalMouseY * this.scale;
        
            drawCallback();
        }, { signal, passive: false });
    }

    fitToBoxes(boxes: AbstractShape[], canvas: HTMLCanvasElement) {
        if (boxes.length === 0) return;
    
        // Step 1: Find bounding box
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
        for (const box of boxes) {
            minX = Math.min(minX, box.x);
            minY = Math.min(minY, box.y);
            maxX = Math.max(maxX, box.x + box.width);
            maxY = Math.max(maxY, box.y + box.height);
        }
    
        const boxWidth = maxX - minX;
        const boxHeight = maxY - minY;
    
        // Step 2: Calculate scale to fit
        const margin = 20; // optional padding around the boxes
        const scaleX = (canvas.width - 2 * margin) / boxWidth;
        const scaleY = (canvas.height - 2 * margin) / boxHeight;
        this.scale = Math.min(scaleX, scaleY);
    
        // Clamp scale to avoid over-zooming
        this.scale = Math.max(0.005, Math.min(50, this.scale));
    
        // Step 3: Center the boxes in the canvas
        const offsetXLogical = minX + boxWidth / 2;
        const offsetYLogical = minY + boxHeight / 2;
    
        this.offsetX = canvas.width / 2 - offsetXLogical * this.scale;
        this.offsetY = canvas.height / 2 - offsetYLogical * this.scale;
    }
}
